import ButtonComponent from "@components/Theme/Button/Button"
import React, {useEffect, useRef} from "react"
import {CSSTransition} from "react-transition-group"

import styles from "./DropdownSelect.module.scss"

type DropdownOption = {
	key: string
	render: React.ReactNode
	onClick?: () => void
	className?: string
}

type Props = {
	id: number
	isShown: number
	setIsShown: (id: number) => void
	options: DropdownOption[]
	buttonContent: React.ReactNode
	className?: string
	activeContext?: "desktop" | "mobile"
	menuClassName?: string
	optionClassName?: string
	selectedOptionClassName?: string
}

const DropdownSelect = React.forwardRef<HTMLDivElement, Props>(
	(
		{
			id,
			isShown,
			setIsShown,
			options,
			buttonContent,
			className = "",
			activeContext = "desktop",
			menuClassName = "",
			optionClassName = "",
			selectedOptionClassName = "",
		},
		externalRef,
	) => {
		const ref = useRef<HTMLDivElement>(null)
		const isOpen = isShown === id

		useEffect(() => {
			const isMobile = window.innerWidth < 1024
			const context = isMobile ? "mobile" : "desktop"

			if (context !== activeContext || !isOpen) return

			const handleClickOutside = (event: MouseEvent) => {
				if (!ref.current || ref.current.contains(event.target as Node))
					return
				setIsShown(-1)
			}

			document.addEventListener("mousedown", handleClickOutside)
			return () => {
				document.removeEventListener("mousedown", handleClickOutside)
			}
		}, [activeContext, isOpen, setIsShown])

		const toggleMenu = () => {
			setIsShown(isOpen ? -1 : id)
		}

		return (
			<div ref={ref} className={`${styles.wrapper} ${className}`}>
				<ButtonComponent
					buttonType={"text"}
					className={[
						styles.selectedOption,
						selectedOptionClassName,
						isOpen ? styles.active : "",
					]
						.filter(Boolean)
						.join(" ")}
					size={"small"}
					onClick={toggleMenu}
				>
					{buttonContent}
				</ButtonComponent>

				<CSSTransition
					in={isOpen}
					timeout={200}
					classNames={{
						enter: styles.enter,
						enterActive: styles.enterActive,
						exit: styles.exit,
						exitActive: styles.exitActive,
					}}
					unmountOnExit
				>
					<div className={`${styles.selectMenu} ${menuClassName}`}>
						{options.map((option) => (
							<div
								key={option.key}
								className={`${optionClassName} ${option.className} ${styles.option}`}
								onClick={option.onClick}
							>
								{option.render}
							</div>
						))}
					</div>
				</CSSTransition>
			</div>
		)
	},
)

export default DropdownSelect
